// P2b tanda 2 — escritura del producto en WooCommerce por el endpoint de catálogo
// (contrato §3.7). La forma del cuerpo la arma formToWooPayload; acá solo va la red.

import { formToWooPayload } from './productWooPayload';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const BASE = `${API_URL}/api/catalog/products`;

// Tanda 3b (defecto 1b): el listado pide TODOS los estados (status=any: publish,
// draft, private y pending; la papelera queda afuera). Sin esto, un producto
// guardado como borrador en WooCommerce era invisible en Neto.
// W2: /api/products lee del ESPEJO (0 llamadas a Woo) — el flag prefetch murió
// con la precarga a Woo; la URL no lo lleva nunca.
export function productsListUrl({ page = 1, perPage = 24, nocache = false } = {}) {
  const params = new URLSearchParams({ per_page: String(perPage), page: String(page), status: 'any' });
  if (nocache) params.set('nocache', '1');
  return `${API_URL}/api/products?${params}`;
}

// status: 'draft' | 'publish'. productId null = crear; con id = editar.
// includeCategories / includeImages: solo se mandan si el usuario los tocó.
// camposTocados (3g): en edición, los campos de texto viajan solo si se tocaron.
export async function saveProductToWoo(form, { productId = null, status = 'draft', includeCategories = true, includeImages = false, camposTocados = null, attributes = null, tipos = undefined, base = null, forzarSobrescribir = false } = {}) {
  const body = formToWooPayload(form, { status, includeCategories, includeImages, soloTocados: productId != null, camposTocados, attributes, ...(tipos ? { tipos } : {}) });
  // P3-10 punto 1 (optimistic lock): la ficha manda lo que tenía al abrir (fecha +
  // snapshot). NO va a Woo — el backend lo usa para detectar un cambio externo. Con
  // forzarSobrescribir el backend salta el chequeo («Sobrescribir»).
  if (productId && base) {
    body.base_date_modified_gmt = base.dateModified || null;
    body.base_valores = base.valores || {};
  }
  if (forzarSobrescribir) body.forzar_sobrescribir = true;
  const res = await fetch(productId ? `${BASE}/${productId}` : BASE, {
    method: productId ? 'PUT' : 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // P3-10 punto 1: conflicto de edición — el error lleva los campos en conflicto y
    // el date_modified_gmt nuevo para que la ficha ofrezca recargar/sobrescribir.
    if (res.status === 409 && data.code === 'conflicto_edicion') {
      const err = new Error(data.error || 'El producto cambió en la tienda desde que abriste la ficha.');
      err.code = 'conflicto_edicion';
      err.conflicto = { cambios: Array.isArray(data.cambios) ? data.cambios : [], dateModified: data.date_modified_gmt || null };
      throw err;
    }
    const msg = data.code ? `${data.error} (${data.code})` : (data.error || 'Error al guardar en WooCommerce');
    // Tanda 3d (defecto 3): el código viaja como campo propio, no solo fundido en
    // el mensaje — la ficha decide por él si el error es "SKU duplicado".
    const err = new Error(msg);
    err.code = data.code;
    throw err;
  }
  return data;
}

// Ajuste 6 (3c) + ajuste 4 (3e): eliminar producto. Sin `definitivo` va a la
// PAPELERA (recuperable, SKU ocupado); con `definitivo` (solo nunca-publicados)
// se borra para siempre y libera el SKU — el servidor re-verifica el estado.
// La confirmación con el nombre la hace el caller antes de llamar.
export async function deleteProductToWoo(productId, { definitivo = false } = {}) {
  const res = await fetch(`${BASE}/${productId}${definitivo ? '?definitivo=1' : ''}`, { method: 'DELETE' });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo eliminar el producto');
  return data;
}

// ── IA-1: IA de la ficha (sugerencias; la IA PROPONE, el usuario acepta) ──────
// ¿Está encendida la IA de la ficha? Lee el flag del GET /api/config (off por defecto).
export async function iaFichaHabilitada() {
  try {
    const r = await fetch(`${API_URL}/api/config`);
    if (!r.ok) return false;
    const d = await r.json().catch(() => ({}));
    return !!(d.ia && d.ia.habilitado);
  } catch { return false; }
}
// Descripción + título SEO + meta (un solo llamado). `tipo` = { nombre, detalles, cierre } del tipo
// elegido en la ficha (gana sobre el arquetipo). Devuelve {descripcion_html, titulo_seo,
// meta_descripcion, en_que_me_base, aviso_generico, ejemplos_usados, uso}.
export async function sugerirTextosIA(productId, tipo = null, base = 'texto') {
  const r = await fetch(`${API_URL}/api/catalog/ia/sugerir-textos`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: productId, tipo, base }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo generar la sugerencia');
  return d;
}
// Alt descriptivo desde la foto (visión). mediaId opcional (default: foto principal).
export async function sugerirAltIA(productId, { mediaId = null, tipo = null } = {}) {
  const r = await fetch(`${API_URL}/api/catalog/ia/sugerir-alt`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: productId, mediaId, tipo }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo generar el texto alternativo');
  return d; // { alt, mediaId, uso }
}
// Desenlace de una sugerencia (aceptado | reescrito | descartado). Fire-and-forget: medir no puede
// romper el flujo de la ficha.
export function feedbackIA(productId, campo, desenlace) {
  return fetch(`${API_URL}/api/catalog/ia/feedback`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ id: productId, campo, desenlace }),
  }).catch(() => {});
}
// Backfill de SEO por lotes.
export async function backfillSeoCandidatos() {
  const r = await fetch(`${API_URL}/api/catalog/ia/backfill-seo/candidatos`);
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(d.error || 'No se pudieron leer los candidatos'); e.status = r.status; throw e; }
  return d; // { total, productos:[{id,name,categorias,tieneDescripcion,seoTitleActual,seoMetaActual}] }
}
export async function backfillSeoGenerar(ids) {
  const r = await fetch(`${API_URL}/api/catalog/ia/backfill-seo/generar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo generar el SEO');
  return d; // { resultados:[{id,name,titulo_seo,meta_descripcion,en_que_me_base,uso}|{id,error}] }
}
export async function backfillSeoAplicar(items) {
  const r = await fetch(`${API_URL}/api/catalog/ia/backfill-seo/aplicar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ items }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo aplicar el SEO');
  return d; // { resultados:[{id,ok}|{id,error}] }
}
export async function backfillSeoDescartar(ids) {
  const r = await fetch(`${API_URL}/api/catalog/ia/backfill-seo/descartar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo descartar');
  return d; // { descartados:n }
}

// ── Mediateca (tanda 3) ───────────────────────────────────────────────────────
export async function searchMedia({ search = '', page = 1 } = {}) {
  const qs = new URLSearchParams({ page: String(page) });
  if (search) qs.set('search', search);
  const res = await fetch(`${API_URL}/api/catalog/media?${qs}`);
  if (!res.ok) throw new Error('No se pudo buscar en la mediateca');
  return res.json();
}

export async function uploadMedia({ imageBase64, imageMime, filename, altText, contexto }) {
  const res = await fetch(`${API_URL}/api/catalog/media`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageBase64, imageMime, filename, altText, contexto }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    // 3f (defecto 1): el mensaje lleva el texto y código REALES de WordPress
    // (el backend ya no los aplana); los campos viajan también sueltos.
    const msg = data.code ? `${data.error} (${data.code})` : (data.error || 'No se pudo subir la imagen');
    const err = new Error(msg);
    err.code = data.code || null;
    err.http = data.http || null;
    throw err;
  }
  return data; // { id, src, alt }
}

// ── P3-5b: motor de consumibles (valida/crea/actualiza por el motor del backend) ──
// `validar` NO escribe: arma el documento y lo pasa por el esquema + reglas de Neto,
// devuelve { ok, errores:[{campo,mensaje}] }. `crear`/`actualizar` escriben por el
// motor; en error devuelven estructura: validación (400 {errores}), gating (409
// {error,mensaje} — sin App Password / Shopify / sin tienda) o motor (502 {paso,causa}).
export async function validarConsumible(modelo) {
  const res = await fetch(`${API_URL}/api/consumibles/validar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(modelo),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.error || 'No se pudo validar'); e.data = data; throw e; }
  return data; // { ok, errores }
}
async function guardarConsumible(ruta, metodo, modelo) {
  const res = await fetch(`${API_URL}${ruta}`, {
    method: metodo, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(modelo),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(data.mensaje || data.causa || data.error || 'No se pudo guardar');
    e.tipo = data.error || null;      // 'validacion' | 'sin_application_password' | 'plataforma_no_soportada' | 'sin_tienda' | 'motor'
    e.errores = data.errores || null; // validación: [{campo,mensaje}]
    e.paso = data.paso || null; e.causa = data.causa || null; e.registro = data.registro || null;
    // P3-5c (409 de P3-10): el producto cambió en la tienda desde que se abrió la ficha. Se
    // expone igual que en el PUT de productos para que la ficha ofrezca «Recargar/Sobrescribir».
    if (res.status === 409 && data.code === 'conflicto_edicion') {
      e.conflicto = { cambios: data.cambios || [], dateModified: data.date_modified_gmt || null };
    }
    throw e;
  }
  return data; // { ok, productoId, variaciones, registro }
}
export const crearConsumible = (modelo) => guardarConsumible('/api/consumibles/crear', 'POST', modelo);
export const actualizarConsumible = (id, modelo) => guardarConsumible(`/api/consumibles/${id}`, 'PUT', modelo);
// P3-5c: LEE un consumible para editarlo → { ok, modelo }. El modelo trae las 13 metas, los
// sabores/packs con su term meta (hex/descriptor/foto/sello) y el grid de variaciones con sus ids.
export async function leerConsumible(id) {
  const res = await fetch(`${API_URL}/api/consumibles/${id}`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) { const e = new Error(data.mensaje || data.causa || data.error || 'No se pudo leer el consumible'); e.tipo = data.error || null; throw e; }
  return data; // { ok, modelo }
}
// ¿La tienda puede guardar consumibles? { puede, motivo } (sin escribir).
export async function capacidadConsumible() {
  const res = await fetch(`${API_URL}/api/consumibles/capacidad`);
  if (!res.ok) return { puede: false, motivo: 'No se pudo verificar la tienda.' };
  return res.json();
}

// ── P3-4: marcas, colecciones, categorías (gestión) ──────────────────────────
async function getJson(ruta) {
  const r = await fetch(`${API_URL}${ruta}`);
  if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || 'Error de red'); }
  return r.json();
}
async function mutar(ruta, method, body) {
  const r = await fetch(`${API_URL}${ruta}`, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo guardar');
  return d;
}
export const listarMarcas = () => getJson('/api/brands');
export const crearMarca = (name) => mutar('/api/brands', 'POST', { name });
export const renombrarMarca = (id, name) => mutar(`/api/brands/${id}`, 'PUT', { name });
export const listarColecciones = () => getJson('/api/collections');
export const crearColeccion = (name) => mutar('/api/collections', 'POST', { name });
export const renombrarColeccion = (id, name) => mutar(`/api/collections/${id}`, 'PUT', { name });
export const renombrarCategoria = (id, body) => mutar(`/api/categories/${id}`, 'PUT', body);
export const listarCategorias = () => getJson('/api/categories?form=1');
export const crearCategoria = (name, parent = 0) => mutar('/api/categories', 'POST', { name, parent });
// P3-7: ejes de variación (atributos) y orden GLOBAL de sus términos.
export const listarEjes = () => getJson('/api/ejes');
export const reordenarEje = (attrId, orden) => mutar(`/api/ejes/${attrId}/orden`, 'PUT', { orden });

// P3-9b: histórico de optimización de un producto (desde la base de datos, visible
// desde cualquier equipo). → { corridas:[{loteId,fecha,fotos,errores,de,a,ahorro}], total }.
export async function historialOptimizacionProducto(productId) {
  const vacio = { corridas: [], total: { fotos: 0, errores: 0, de: 0, a: 0, ahorro: 0 } };
  try {
    const r = await fetch(`${API_URL}/api/catalog/optimizaciones/${productId}`);
    if (!r.ok) return vacio;
    const d = await r.json().catch(() => ({}));
    return { corridas: d.corridas || [], total: d.total || vacio.total };
  } catch { return vacio; }
}

// P3-9b: completa el texto alternativo vacío de las fotos del producto (nombre, o
// nombre + color de la variación) en la mediateca; no reemplaza los existentes.
export async function completarTextosAlternativos(productoId) {
  const r = await fetch(`${API_URL}/api/catalog/completar-alt`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ productoId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudieron completar los textos alternativos');
  return d;
}

// P3-9b: revierte una corrida (vuelve a las fotos originales, borra las optimizadas).
export async function revertirOptimizacion(loteId) {
  const r = await fetch(`${API_URL}/api/catalog/optimizaciones/revertir`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ loteId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo revertir');
  return d;
}

// P3-9b: borra de la mediateca las fotos ORIGINALES de una corrida (ya no se podrá revertir).
export async function borrarOriginalesOptimizacion(loteId) {
  const r = await fetch(`${API_URL}/api/catalog/optimizaciones/borrar-originales`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ loteId }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudieron borrar las originales');
  return d;
}

// P3-9b: registra en la base de datos una foto que falló al optimizar (para el
// histórico). Best-effort: no interrumpe el flujo si falla.
export async function registrarOptimizacionFallo(payload) {
  try {
    await fetch(`${API_URL}/api/catalog/optimizacion-fallo`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
    });
  } catch { /* best effort */ }
}

// P3-9b (masiva): estado del trabajo de optimización (polling del panel).
export async function masivaEstado() {
  try {
    const r = await fetch(`${API_URL}/api/catalog/optimizacion-masiva/estado`);
    if (!r.ok) return { trabajo: null };
    const d = await r.json().catch(() => ({}));
    return { trabajo: d.trabajo || null };
  } catch { return { trabajo: null }; }
}
// Candidatas (productos + fotos) del alcance, para la confirmación.
export async function masivaCandidatas(alcance, filtroIds) {
  try {
    const qs = new URLSearchParams({ alcance });
    if (alcance === 'filtro' && filtroIds && filtroIds.length) qs.set('filtroIds', filtroIds.join(','));
    const r = await fetch(`${API_URL}/api/catalog/optimizacion-masiva/candidatas?${qs}`);
    if (!r.ok) return { productos: 0, fotos: 0 };
    return r.json();
  } catch { return { productos: 0, fotos: 0 }; }
}
export async function masivaIniciar(alcance, filtroIds) {
  const r = await fetch(`${API_URL}/api/catalog/optimizacion-masiva/iniciar`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ alcance, filtroIds }),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo iniciar');
  return d;
}
export async function masivaPausarReanudar(accion) {
  const r = await fetch(`${API_URL}/api/catalog/optimizacion-masiva/${accion}`, { method: 'POST' });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(d.error || 'No se pudo cambiar el estado');
  return d;
}

// P3-9b (pantalla, antesala de la masiva): fotos pendientes de optimizar por
// producto, del censo guardado. SOLO LECTURA; si falla, mapa vacío.
export async function fotosPendientesProducto() {
  try {
    const r = await fetch(`${API_URL}/api/catalog/fotos-pendientes`);
    if (!r.ok) return { pendientes: {}, censo_generado_en: null };
    const d = await r.json().catch(() => ({}));
    return { pendientes: d.pendientes || {}, censo_generado_en: d.censo_generado_en || null };
  } catch { return { pendientes: {}, censo_generado_en: null }; }
}

// P3-9b (escritura): optimizar UNA foto. Sube el JPG nuevo (base64) y el backend
// lo reapunta en todos los productos que usan la vieja; conserva la vieja (revert).
export async function optimizarFoto(payload) {
  const r = await fetch(`${API_URL}/api/catalog/optimizar-foto`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const d = await r.json().catch(() => ({}));
  if (!r.ok) { const e = new Error(d.error || 'No se pudo optimizar la foto'); e.code = d.code; throw e; }
  return d; // { nuevoId, nuevoSrc, productos:[{id,name}], variaciones, fallos, registroId }
}

// P3-9b (preview): en qué productos se usa cada foto (aviso de reúso «todas o
// ninguna»). SOLO LECTURA; si falla, devuelve {} (el preview igual corre).
export async function fotosUsos(urls) {
  try {
    const r = await fetch(`${API_URL}/api/catalog/fotos-usos`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ urls }),
    });
    if (!r.ok) return {};
    const d = await r.json().catch(() => ({}));
    return d.usos || {};
  } catch { return {}; }
}

// ── P3-9a: descargar fotos del producto como .zip (sin recomprimir) ──────────
// El nombre del archivo lo decide el FRONTEND (el a.download gana sobre
// Content-Disposition en una descarga por blob, y además el header no viaja
// cross-origin). La ficha pasa el slug del producto; el listado, el nombre base
// del filtro (<filtro>-fotos-<fecha>).
export function slugProducto(s) {
  return String(s || '').toLowerCase()
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}
function dispararDescarga(blob, nombre) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = nombre;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}
export async function descargarFotosProducto(productId, slug) {
  const res = await fetch(`${API_URL}/api/catalog/products/${productId}/fotos-zip`);
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'No se pudieron descargar las fotos'); }
  const nombre = `${slugProducto(slug) || 'producto'}-fotos.zip`;
  dispararDescarga(await res.blob(), nombre);
}
// `baseNombre` = «<filtro>-fotos-<fecha>»; es el nombre del zip Y de la carpeta raíz.
export async function descargarFotosLote(ids, baseNombre) {
  const base = slugProducto(baseNombre) || 'catalogo';
  const res = await fetch(`${API_URL}/api/catalog/fotos-zip`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ids, carpetaRaiz: base }),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'No se pudieron descargar las fotos'); }
  dispararDescarga(await res.blob(), `${base}.zip`);
}

// ── P3-8 «Preparar imágenes» ─────────────────────────────────────────────────
// Descarga UN JPG ya preparado en el navegador (dataURL) con su nombre SEO.
export function descargarImagenPreparada(dataUrl, nombre) {
  const b64 = String(dataUrl || '').replace(/^data:[^,]+,/, '');
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  dispararDescarga(new Blob([bytes], { type: 'image/jpeg' }), /\.jpe?g$/i.test(nombre) ? nombre : `${nombre || 'imagen'}.jpg`);
}
// Empaqueta en un .zip VARIAS imágenes preparadas (bytes van en el body — aún no
// están en ninguna parte). imagenes = [{ nombre, base64 (dataURL o base64 pelado) }].
export async function descargarImagenesZip(imagenes) {
  const res = await fetch(`${API_URL}/api/catalog/preparar-zip`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ imagenes }),
  });
  if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'No se pudo armar el zip'); }
  dispararDescarga(await res.blob(), 'imagenes-preparadas.zip');
}

// P3-1 (punto 6): reporta un fallo de subida a subidas_imagenes — FIRE AND
// FORGET a propósito: registrar el fallo nunca puede agregar un segundo fallo
// visible encima del primero.
export function reportarFalloSubida(datos) {
  return fetch(`${API_URL}/api/catalog/subidas-imagenes`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(datos),
  }).catch(() => {});
}

// P3-1 (punto 4): trae el ARCHIVO de una imagen de la mediateca por el backend
// (la URL pública de WordPress es cross-origin y mancharía el canvas del
// encuadre) y lo devuelve como dataURL listo para editar.
export async function fetchMediaArchivoDataUrl(id) {
  const res = await fetch(`${API_URL}/api/catalog/media/${id}/archivo`);
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(data.error || 'No se pudo traer la imagen de la mediateca');
  }
  const blob = await res.blob();
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = () => reject(new Error('No se pudo leer la imagen'));
    fr.readAsDataURL(blob);
  });
}

export async function updateMediaAlt(id, altText) {
  const res = await fetch(`${API_URL}/api/catalog/media/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ altText }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo actualizar el texto alternativo');
  return data;
}

// Imagen por variación: envía SOLO la imagen (el resto de la variación no se toca).
export async function saveVariationImage(productId, variationId, imageId) {
  const res = await fetch(`${API_URL}/api/catalog/products/${productId}/variations/${variationId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ imageId }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo guardar la imagen de la variación');
  return data;
}

// ── Edición de variaciones (tanda 4, PARTE B) ────────────────────────────────
// Una variación tocada → PUT con SOLO sus campos tocados (la presencia de la
// clave es la marca, regla 3g).
export async function saveVariation(productId, body) {
  const { id, ...campos } = body || {};
  const res = await fetch(`${API_URL}/api/catalog/products/${productId}/variations/${id}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(campos),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.code ? `${data.error} (${data.code})` : (data.error || 'Error al guardar la variación'));
  return data;
}

// Crear una variación (tanda 7b): attributeName + option obligatorios; el
// resto (sku, precio, stock, gtin, imageId) es opcional — misma lista blanca.
export async function createVariation(productId, body) {
  const res = await fetch(`${API_URL}/api/catalog/products/${productId}/variations`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.code ? `${data.error} (${data.code})` : (data.error || 'Error al crear la variación'));
  return data;
}

// Varias tocadas → UNA llamada al batch.
export async function saveVariationsBatch(productId, items) {
  const res = await fetch(`${API_URL}/api/catalog/products/${productId}/variations/batch`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ update: items }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.code ? `${data.error} (${data.code})` : (data.error || 'Error al guardar las variaciones'));
  return data;
}

// Eliminar variación (force=true siempre: las variaciones no tienen papelera).
export async function deleteVariation(productId, variationId) {
  const res = await fetch(`${API_URL}/api/catalog/products/${productId}/variations/${variationId}`, {
    method: 'DELETE',
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo eliminar la variación');
  return data;
}

// Papelera de Catálogo (tanda 4, PARTE C).
export async function fetchTrash() {
  const res = await fetch(`${API_URL}/api/catalog/trash`);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'No se pudo leer la papelera');
  return Array.isArray(data) ? data : [];
}
